import React, { useEffect, useMemo, useState } from 'react'
import { Check, Loader2 } from 'lucide-react'
import {
  PqResultRef,
  PqStepResults,
  fetchPqResults,
} from './pqResultsSource'

/** Stored config: the chosen source PQ step (persists across navigation) +
 * the selected result refs (map to legacy `dynamics`). */
export type PqHideConfig = {
  stepId?: number
  refs?: PqResultRef[]
}

/** Tolerate the earlier array-only shape. */
const normalize = (v?: PqHideConfig | PqResultRef[]): PqHideConfig =>
  Array.isArray(v) ? { refs: v } : v ?? {}

/**
 * Cross-step PQ hide-list picker (referral card). Two stages: choose the
 * source Personality Quiz step, then search + tick its results. Discovers
 * steps from the admin step list (no backend changes). Stores
 * {stepId, refs:[{stepId,resultId,label}]}; production maps resultId to the
 * legacy `dynamics` the referral runtime already filters on.
 */
export const PqHideResultsField = ({
  value,
  onChange,
}: {
  value?: PqHideConfig | PqResultRef[]
  onChange: (v: PqHideConfig) => void
}) => {
  const config = normalize(value)
  const refs = config.refs ?? []
  const [steps, setSteps] = useState<PqStepResults[] | null>(null)
  const [search, setSearch] = useState('')

  useEffect(() => {
    let live = true
    fetchPqResults().then(s => live && setSteps(s))
    return () => {
      live = false
    }
  }, [])

  const step = useMemo(
    () => steps?.find(s => s.stepId === config.stepId),
    [steps, config.stepId],
  )
  const has = (rid: string) => refs.some(r => r.resultId === rid)

  const setStep = (stepId?: number) =>
    onChange({ ...config, stepId })

  const toggle = (ref: PqResultRef) =>
    onChange({
      ...config,
      refs: has(ref.resultId)
        ? refs.filter(r => r.resultId !== ref.resultId)
        : [...refs, ref],
    })

  const visibleResults = (step?.results ?? []).filter(r =>
    r.label.toLowerCase().includes(search.trim().toLowerCase()),
  )

  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: 'rgba(0,0,0,0.55)',
          marginBottom: 6,
          lineHeight: 1.4,
        }}
      >
        Hide this card when the visitor got one of these Personality Quiz
        results (earlier in the break). <b>{refs.length}</b> selected.
      </div>

      {steps === null ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>
          <Loader2 size={13} /> Loading quiz steps…
        </div>
      ) : steps.length === 0 ? (
        <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>
          No Personality Quiz steps found on this site.
        </div>
      ) : (
        <>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'rgba(0,0,0,0.5)' }}>
            Quiz step
          </label>
          <select
            value={config.stepId ?? ''}
            onChange={e =>
              setStep(e.target.value ? Number(e.target.value) : undefined)
            }
            style={{
              width: '100%',
              margin: '4px 0 8px',
              padding: '5px 6px',
              fontSize: 13,
              border: '1px solid rgba(0,0,0,0.15)',
              borderRadius: 4,
            }}
          >
            <option value="">— choose a quiz step —</option>
            {steps.map(s => (
              <option key={s.stepId} value={s.stepId}>
                Step {s.stepId} · {s.stepLabel}
              </option>
            ))}
          </select>

          {step && (
            <>
              <input
                type="text"
                value={search}
                placeholder="Search results…"
                onChange={e => setSearch(e.target.value)}
                style={{
                  width: '100%',
                  marginBottom: 6,
                  padding: '5px 6px',
                  fontSize: 13,
                  border: '1px solid rgba(0,0,0,0.15)',
                  borderRadius: 4,
                }}
              />
              <div
                style={{
                  maxHeight: 260,
                  overflowY: 'auto',
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: 6,
                  padding: 4,
                }}
              >
                {visibleResults.length === 0 && (
                  <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.5)', padding: 4 }}>
                    No matching results.
                  </div>
                )}
                {visibleResults.map(r => {
                  const on = has(r.resultId)
                  return (
                    <div
                      key={r.resultId}
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        toggle({
                          stepId: step.stepId,
                          stepLabel: step.stepLabel,
                          resultId: r.resultId,
                          label: r.label,
                        })
                      }
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '6px 8px',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 13,
                        background: on ? 'rgba(67,99,216,0.12)' : 'transparent',
                      }}
                    >
                      <span
                        style={{
                          flex: '0 0 auto',
                          width: 16,
                          height: 16,
                          borderRadius: 4,
                          border: '2px solid #4363d8',
                          background: on ? '#4363d8' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {on && <Check size={11} color="#fff" strokeWidth={3} />}
                      </span>
                      {r.label}
                    </div>
                  )
                })}
              </div>
            </>
          )}

          {refs.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(0,0,0,0.5)', marginBottom: 4 }}>
                Hiding for:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {refs.map(r => (
                  <span
                    key={r.resultId}
                    onClick={() =>
                      onChange({
                        ...config,
                        refs: refs.filter(x => x.resultId !== r.resultId),
                      })
                    }
                    title="Remove"
                    style={{
                      fontSize: 11,
                      padding: '2px 8px',
                      borderRadius: 999,
                      background: 'rgba(67,99,216,0.12)',
                      color: '#4363d8',
                      cursor: 'pointer',
                    }}
                  >
                    {r.label} ✕
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
